import type {
  CourseDiffEntity, CourseDiffKind, MovedOutEntity, NoDeliveryEntity,
} from 'src/entities/PlanningsDelivery.entity';

/**
 * 固定コースとの差分の表示と絞り込み（log_web_issues#353）。
 * 判定は API（CourseDiffResolver）が済ませた値を読むだけで、ここでは比べない。
 */

export const COURSE_DIFF_FILTER_VALUES = ['all', 'unchanged', 'course_changed', 'sequence_changed'] as const;
export type CourseDiffFilterValue = typeof COURSE_DIFF_FILTER_VALUES[number];
export const COURSE_DIFF_FILTER_LABELS: Record<CourseDiffFilterValue, string> = {
  all: 'すべて',
  unchanged: '固定どおり',
  course_changed: 'コース変更あり',
  sequence_changed: '配送順変更あり',
};

/** 回転ごとの移動元の一覧（勤務一覧の rotations[] から行 VM に写したもの） */
export type RotationCourseChanges = {
  currentCourseId: number | null;
  movedOut: MovedOutEntity[];
  noDelivery: NoDeliveryEntity[];
};

/**
 * 伝票に写したコードで「101-3」。同じコードで ID が違う（1 便 → 2 便）ときだけ便を付けて「101(1便)-3」
 * （D15 ルール 4「番号に回転は付けない」を守りつつ、D21 決定 18 の便違いが読めるように）
 */
export function fixedCourseLabel(diff: CourseDiffEntity): string {
  const code = diff.fixedCourseCode ?? '?';
  const sameCodeOtherTrip = diff.currentCourseCode === diff.fixedCourseCode
    && diff.currentCourseId !== diff.fixedCourseId
    && diff.fixedTripNo != null;
  const trip = sameCodeOtherTrip ? `(${diff.fixedTripNo}便)` : '';
  const seq = diff.fixedSequenceNo != null ? `-${diff.fixedSequenceNo}` : '';
  return `${code}${trip}${seq}`;
}

/**
 * カードのバッジ。leg_run ごとの差分を全部受け、重複を除いて入力順に返す。変更なしは出さない。
 * ダイヤ外のときは「ダイヤ外」も含めて返し、コース変更の元と 1 つにまとめる（log_web_issues#401）:
 * 「ダイヤ外（元：101-3）」、元が無ければ「ダイヤ外」。ダイヤ内のコース変更は「元：101-3」
 * 配送順変更も同じ「元：101-4」（PM フィードバック 2026-10-27。以前は「固定順: 4」）。ダイヤ外とはまとめず別のバッジ
 * ダイヤ外は選んだダイヤのどのコースにも無い店舗だけ（#403 の 4）で、固定コースはダイヤのコースからしか付かないので、
 * 「ダイヤ外（元：…）」の組み合わせは実質出ない（ユーザー了承 2026-10-27。まとめる組み立ては残す）
 *
 * Why not 運行指示書の「元: 101-3」も全角にする: 運行指示書は今回のフィードバックの対象外（2026-10-26 ユーザー決定）
 */
export function courseDiffBadgeLabels(diffs: readonly CourseDiffEntity[], routeDeviation = false): string[] {
  const origins: string[] = [];
  const sequenceLabels: string[] = [];
  for (const diff of diffs) {
    if (diff.kind === 'course_changed') {
      const origin = fixedCourseLabel(diff);
      if (!origins.includes(origin)) origins.push(origin);
    } else if (diff.kind === 'sequence_changed') {
      const label = `元：${fixedCourseLabel(diff)}`;
      if (!sequenceLabels.includes(label)) sequenceLabels.push(label);
    }
  }
  const originLabels = routeDeviation
    ? [origins.length > 0 ? `ダイヤ外（元：${origins.join('・')}）` : 'ダイヤ外']
    : origins.map((origin) => `元：${origin}`);
  return [...originLabels, ...sequenceLabels.filter((label) => !originLabels.includes(label))];
}

type CardWithDiffs = { courseDiffs?: readonly CourseDiffEntity[] };

/** 行のカードのどれかに kind の差分が 1 件でもあるか（1 枚に複数の leg_run が畳まれていても落とさない） */
export function hasCourseDiffKind(cards: readonly CardWithDiffs[], kind: CourseDiffKind): boolean {
  return cards.some((card) => (card.courseDiffs ?? []).some((diff) => diff.kind === kind));
}

/** 変更案件フィルター（決定 4: 回転の行で絞る）の行の判定 */
export function courseDiffFilterMatches(
  source: { cards: readonly CardWithDiffs[]; courseChanges?: RotationCourseChanges },
  filter: CourseDiffFilterValue,
): boolean {
  const movedOutCount = source.courseChanges?.movedOut.length ?? 0;
  switch (filter) {
    case 'all':
      return true;
    case 'unchanged':
      // 本日なしは変更に数えない（A と C はいつも通り）
      return source.courseChanges?.currentCourseId != null
        && !hasCourseDiffKind(source.cards, 'course_changed')
        && !hasCourseDiffKind(source.cards, 'sequence_changed')
        && movedOutCount === 0;
    case 'course_changed':
      return hasCourseDiffKind(source.cards, 'course_changed') || movedOutCount > 0;
    case 'sequence_changed':
      return hasCourseDiffKind(source.cards, 'sequence_changed');
    default: {
      const exhaustive: never = filter;
      return exhaustive;
    }
  }
}
